/**
 * Rich paste through an allowlist (decisions 44 and 66 bullet 3; table H in the part 10 log). Pasted or dropped HTML
 * becomes source with only the formatting the book has: paragraphs, headings, quotes, lists, scene breaks, emphasis,
 * strong, small caps, hard breaks, and links to http, https and mailto. Everything else is its text, escaped, or
 * nothing at all (scripts, styles and the like, H5). The source is checked by parsing it: where a paragraph's or
 * heading's text doesn't read back as the fragment's, it is written again without inline formatting (H10).
 */
import { isAllowedLink, parseChapter } from '@cockatrice/core';
import { readHtml } from './html.ts';
import type { HtmlElement, HtmlNode } from './html.ts';
import { unitsOf } from './units.ts';

/** H8: HTML longer than this pastes as plain text. */
export const MAX_HTML_LENGTH = 4 * 1024 * 1024;

/** H5: elements dropped with everything in them. */
const DROPPED: ReadonlySet<string> = new Set([
  'script', 'style', 'template', 'noscript', 'title', 'head', 'iframe', 'object', 'embed', 'svg', 'math', 'xmp', 'noembed',
  'noframes', 'plaintext', 'textarea', 'select', 'img', 'picture', 'video', 'audio', 'canvas',
]);

/** Elements that start a block of their own; any other element is inline (H6). */
const BLOCKS: ReadonlySet<string> = new Set([
  'p', 'div', 'section', 'article', 'header', 'footer', 'main', 'nav', 'aside', 'address', 'figure', 'figcaption', 'dl', 'dt',
  'dd', 'table', 'thead', 'tbody', 'tfoot', 'tr', 'caption', 'form', 'fieldset', 'center', 'h1', 'h2', 'h3', 'h4', 'h5',
  'h6', 'blockquote', 'ul', 'ol', 'li', 'hr', 'pre', 'body', 'html',
]);

/** Table cells: inline, but text apart from their neighbours' (H2; *deep-fix r6*). */
const CELLS: ReadonlySet<string> = new Set(['td', 'th']);

/** The inline formatting in force (H3, H4). */
interface Style {
  readonly italic: boolean;
  readonly bold: boolean;
  readonly smallCaps: boolean;
  readonly href: string | null;
}

/** A piece of inline content: text in one style, or a hard break. */
type Run = { readonly text: string; readonly style: Style } | { readonly br: true };

/** A block of the pasted fragment. */
type Out =
  | { readonly kind: 'paragraph'; readonly runs: readonly Run[]; readonly inline?: boolean }
  | { readonly kind: 'heading'; readonly level: number; readonly runs: readonly Run[] }
  | { readonly kind: 'quote'; readonly blocks: readonly Out[] }
  | { readonly kind: 'list'; readonly ordered: boolean; readonly items: readonly (readonly Out[])[] }
  | { readonly kind: 'scene' };

const PLAIN: Style = { italic: false, bold: false, smallCaps: false, href: null };

/** The declarations of a `style` attribute that matter here, lowercase. */
function declarations(style: string | undefined): Map<string, string> {
  const out = new Map<string, string>();
  for (const part of (style ?? '').split(';')) {
    const colon = part.indexOf(':');
    if (colon > 0) out.set(part.slice(0, colon).trim().toLowerCase(), part.slice(colon + 1).trim().toLowerCase());
  }
  return out;
}

/** A `font-weight` as bold or not, or null when it says neither. */
function weight(value: string | undefined): boolean | null {
  if (value === undefined) return null;
  if (value === 'bold' || value === 'bolder') return true;
  if (value === 'normal' || value === 'lighter') return false;
  const number = Number.parseInt(value, 10);
  return Number.isNaN(number) ? null : number >= 600;
}

/** The style inside `element` (H3, H4). */
function styleOf(element: HtmlElement, outer: Style): Style {
  const css = declarations(element.attributes.get('style'));
  const name = element.name;
  let { italic, bold, smallCaps, href } = outer;
  if (name === 'em' || name === 'i' || name === 'cite' || name === 'var' || name === 'dfn') italic = true;
  if (name === 'strong' || name === 'b') bold = true;
  const fontStyle = css.get('font-style');
  if (fontStyle === 'italic' || fontStyle === 'oblique') italic = true;
  else if (fontStyle === 'normal') italic = false;
  bold = weight(css.get('font-weight')) ?? bold;
  const variant = css.get('font-variant-caps') ?? css.get('font-variant');
  if (variant === 'small-caps') smallCaps = true;
  else if (variant === 'normal') smallCaps = false;
  if (name === 'a') {
    const target = (element.attributes.get('href') ?? '').trim();
    // H4, L2: the one shared check (decision 44).
    href = isAllowedLink(target) ? target : null;
  }
  return { italic, bold, smallCaps, href };
}

/** Reads a fragment's nodes as blocks; inline content outside any block becomes paragraphs. */
class Reader {
  readonly blocks: Out[] = [];
  private runs: Run[] = [];
  /** Inside `pre`: whitespace and line ends kept. */
  private readonly pre: boolean;
  /** The fragment's own level, whose paragraphs are inline content no block element holds (H1, *deep-fix r18*). */
  private readonly top: boolean;

  constructor(pre: boolean, top = false) {
    this.pre = pre;
    this.top = top;
  }

  /** Ends the paragraph being read, if it holds anything. */
  flush(): void {
    if (this.runs.some((run) => !('br' in run) && htmlTrim(run.text) !== '')) this.blocks.push({ kind: 'paragraph', runs: this.runs, inline: this.top });
    this.runs = [];
  }

  read(nodes: readonly HtmlNode[], style: Style): void {
    for (const node of nodes) {
      if (node.kind === 'text') {
        const text = this.pre ? node.text : node.text.replace(/[\t\n\f\r ]+/gu, ' ');
        if (this.pre && text.includes('\n')) {
          for (const [i, line] of text.split('\n').entries()) {
            if (i > 0) this.runs.push({ br: true });
            this.runs.push({ text: line, style });
          }
        } else {
          this.runs.push({ text, style });
        }
        continue;
      }
      if (DROPPED.has(node.name)) continue;
      if (node.name === 'br') {
        this.runs.push({ br: true });
        continue;
      }
      // The document's own wrappers (a clipboard's `<html><body>`) hold the fragment, not a block of it.
      if (!BLOCKS.has(node.name) || node.name === 'html' || node.name === 'body') {
        // A cell's text stays a word of its own: a blank on each side, collapsed with any other.
        if (CELLS.has(node.name)) this.runs.push({ text: ' ', style: PLAIN });
        this.read(node.children, styleOf(node, style));
        if (CELLS.has(node.name)) this.runs.push({ text: ' ', style: PLAIN });
        continue;
      }
      this.flush();
      const inner = styleOf(node, style);
      const heading = /^h([1-6])$/u.exec(node.name);
      if (heading !== null) {
        this.blocks.push({ kind: 'heading', level: Number(heading[1]), runs: inlineOf(node.children, inner) });
      } else if (node.name === 'hr') {
        this.blocks.push({ kind: 'scene' });
      } else if (node.name === 'blockquote') {
        this.blocks.push({ kind: 'quote', blocks: blocksOf(node.children, inner, this.pre) });
      } else if (node.name === 'ul' || node.name === 'ol') {
        // Items make the list; anything else in it stands between lists, as its own blocks.
        let items: Out[][] = [];
        const end = (): void => {
          if (items.length > 0) this.blocks.push({ kind: 'list', ordered: node.name === 'ol', items });
          items = [];
        };
        for (const child of node.children) {
          const blocks = child.kind === 'element' && child.name === 'li' ? blocksOf(child.children, styleOf(child, inner), this.pre) : null;
          if (blocks !== null) {
            if (blocks.length > 0) items.push(blocks);
            continue;
          }
          const between = blocksOf([child], inner, this.pre);
          if (between.length === 0) continue;
          end();
          this.blocks.push(...between);
        }
        end();
      } else {
        this.blocks.push(...blocksOf(node.children, inner, this.pre || node.name === 'pre'));
      }
    }
  }
}

function blocksOf(nodes: readonly HtmlNode[], style: Style, pre: boolean): Out[] {
  const reader = new Reader(pre);
  reader.read(nodes, style);
  reader.flush();
  return reader.blocks;
}

/** Every run of a block and the blocks inside it, in order. */
function runsOf(block: Out): Run[] {
  switch (block.kind) {
    case 'paragraph':
    case 'heading':
      return [...block.runs];
    case 'quote':
      return block.blocks.flatMap(runsOf);
    case 'list':
      return block.items.flatMap((item) => item.flatMap(runsOf));
    case 'scene':
      return [];
  }
}

/** A heading's inline content: whatever blocks it holds read as one line, breaks as blanks. */
function inlineOf(nodes: readonly HtmlNode[], style: Style): Run[] {
  return blocksOf(nodes, style, false).flatMap((block, i) => {
    const runs = runsOf(block).map((run) => ('br' in run ? { text: ' ', style: PLAIN } : run));
    return i > 0 ? [{ text: ' ', style: PLAIN }, ...runs] : runs;
  });
}

/**
 * Text without HTML's whitespace at its ends: space, tab and line ends only. A no-break or ideographic space is text HTML
 * shows, and stays (H1, *deep-fix r17*: a Japanese paragraph's full-width indent).
 */
function htmlTrim(text: string): string {
  return text.replace(/^[\t\n\f\r ]+|[\t\n\f\r ]+$/gu, '');
}

/** Text as text wherever it lands: every ASCII punctuation character escaped. */
function escapeText(text: string): string {
  return text.replace(/[!-/:-@[-`{-~]/gu, (char) => `\\${char}`);
}

/** Characters that end or break a bare destination: blanks, parentheses, angle brackets, backslashes. */
const BREAKS_DESTINATION = /[\s()<>\\]/u;

/** A destination with the characters that would end or break it, and control characters, percent-encoded. */
function destination(href: string): string {
  let out = '';
  for (const char of href) {
    const code = char.charCodeAt(0);
    out += code < 0x20 || code === 0x7f || BREAKS_DESTINATION.test(char) ? `%${code.toString(16).toUpperCase().padStart(2, '0')}` : char;
  }
  return out;
}

/** Inline runs as source lines, formatting written when `formatted` (H1, H3, H4). */
function inlineSource(runs: readonly Run[], formatted: boolean): string[] {
  // Runs of one style are written together; a break ends them.
  let group: { text: string; style: Style }[] = [];
  const groups = [group];
  for (const run of runs) {
    if ('br' in run) {
      group = [];
      groups.push(group);
      continue;
    }
    const last = group.at(-1);
    const style = formatted ? run.style : PLAIN;
    // H1: blanks collapse across elements too, as HTML shows them.
    const text = last?.text.endsWith(' ') === true && run.text.startsWith(' ') ? run.text.slice(1) : run.text;
    if (last !== undefined && sameStyle(last.style, style)) last.text += text;
    else group.push({ text, style });
  }
  // A line's blanks at its ends read as nothing; an empty line stays, for the caller to read.
  return groups.map((group) => htmlTrim(group.map(({ text, style }) => styled(text, style)).join('')));
}

function sameStyle(a: Style, b: Style): boolean {
  return a.italic === b.italic && a.bold === b.bold && a.smallCaps === b.smallCaps && a.href === b.href;
}

/** Text in a style: blanks at its ends stay outside the marks, where they can't break them. */
function styled(text: string, style: Style): string {
  // Every Unicode blank stays outside the marks, where flanking can't be broken by it; it is still written, as text.
  const core = text.trim();
  if (core === '') return text;
  const lead = text.slice(0, text.indexOf(core.charAt(0)));
  const trail = text.slice(text.lastIndexOf(core.charAt(core.length - 1)) + 1);
  const open = `${style.bold ? '**' : ''}${style.italic ? '*' : ''}${style.smallCaps ? '^^' : ''}`;
  const close = `${style.smallCaps ? '^^' : ''}${style.italic ? '*' : ''}${style.bold ? '**' : ''}`;
  let inner = `${open}${escapeText(core)}${close}`;
  if (style.href !== null) inner = `[${inner}](${destination(style.href)})`;
  return `${lead}${inner}${trail}`;
}

/** Blocks as source lines, blocks apart by a blank line. */
function blockSource(blocks: readonly Out[], formatted: boolean): string[] {
  const out: string[] = [];
  for (const block of blocks) {
    const lines = blockLines(block, formatted);
    if (lines.length === 0) continue;
    if (out.length > 0) out.push('');
    out.push(...lines);
  }
  return out;
}

function blockLines(block: Out, formatted: boolean): string[] {
  if (!formatted || (block.kind !== 'paragraph' && block.kind !== 'heading')) return linesOf(block, formatted);
  // H10 per paragraph and heading (*deep-fix r6*): formatting that doesn't read back is dropped there, kept elsewhere.
  const lines = linesOf(block, true);
  const plain = linesOf(block, false);
  return textOf(lines.join('\n')) === textOf(plain.join('\n')) ? lines : plain;
}

function linesOf(block: Out, formatted: boolean): string[] {
  switch (block.kind) {
    case 'paragraph': {
      // An empty line (`<br><br>`) ends a paragraph, as a blank line does in pasted text (A4; *deep-fix r5*); a break with
      // nothing on one side goes.
      const paragraphs = inlineSource(block.runs, formatted)
        .join('\n')
        .split(/\n{2,}/u)
        .map((text) => text.split('\n').filter((line) => line !== ''))
        .filter((lines) => lines.length > 0);
      return paragraphs.flatMap((lines, k) => [...(k > 0 ? [''] : []), ...lines.map((line, i) => (i < lines.length - 1 ? `${line}\\` : line))]);
    }
    case 'heading': {
      const text = inlineSource(block.runs, formatted)
        .filter((line) => line !== '')
        .join(' ');
      return text === '' ? [] : [`${'#'.repeat(block.level)} ${text}`];
    }
    case 'quote':
      return blockSource(block.blocks, formatted).map((line) => (line === '' ? '>' : `> ${line}`));
    case 'list':
      return block.items.flatMap((item, i) => {
        const marker = block.ordered ? `${i + 1}. ` : '- ';
        const lines = blockSource(item, formatted);
        return lines.map((line, k) => (k === 0 ? `${marker}${line}` : line === '' ? '' : `${' '.repeat(marker.length)}${line}`));
      });
    case 'scene':
      return ['***'];
  }
}

/** The fragment's text as the writer sees it, for H10: every run's text, HTML's whitespace left out. */
function textOf(source: string): string {
  return unitsOf(parseChapter(source))
    .filter((unit) => unit.kind === 'text')
    .map((unit) => unit.show)
    .join('')
    .replace(/[\t\n\f\r ]+/gu, '');
}

/**
 * The source a pasted HTML fragment puts in (table H), or null when it is too long (H8) or holds no text: the caller
 * pastes its plain text instead.
 */
export function richSource(html: string): string | null {
  if (html.length > MAX_HTML_LENGTH) return null;
  const reader = new Reader(false, true);
  reader.read(readHtml(html).children, PLAIN);
  reader.flush();
  const { blocks } = reader;
  if (htmlTrim(blockSource(blocks, false).join('\n')) === '') return null;
  // H1 (*deep-fix r18*): a blank at the fragment's outer edge, where its first or last paragraph goes in inline (a word
  // copied with the space after it), is the writer's: kept, as a reference its parse keeps.
  const first = blocks[0];
  const last = blocks.at(-1);
  const lead = first?.kind === 'paragraph' && first.inline === true && /^[\t\n\f\r ]/u.test(edgeText(first.runs));
  const trail = last?.kind === 'paragraph' && last.inline === true && /[\t\n\f\r ]$/u.test(edgeText(last.runs));
  // H10 is checked per paragraph and heading (`blockLines`): blocks stand apart (a blank line or a container's marks
  // between them) and inline markup never crosses them, so the fragment reads back as each of them does.
  return `${lead ? '&#32;' : ''}${blockSource(blocks, true).join('\n')}${trail ? '&#32;' : ''}`;
}

/** Runs' text from the first to the last run holding more than blanks: a blank-only run is the HTML's layout. */
function edgeText(runs: readonly Run[]): string {
  const texts = runs.flatMap((run) => ('br' in run || htmlTrim(run.text) === '' ? [] : [run.text]));
  return texts.join('');
}
